import { AsyncPipe, CommonModule, NgClass, NgIf } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  GridSectionComponent,
} from '@domgen/dgx-fe-components/grid';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import { SeoPage } from '@domgen/dgx-fe-seo';
import { ArticleSearchFormComponent } from '@self-fix/feature-article-search';
import { ApplianceDetailsComponentService } from '../../services/appliance-details-component/appliance-details-component.service';
import { CategoryAccordionBlockComponent } from '@domgen/dgx-fe-components/category';
import {
  MultipleButtonContentCardComponent,
  PopularLinkListBlockComponent,
} from '@domgen/dgx-fe-components/content';
import { VideoListBlockComponent } from '@self-fix/feature-components';
import {
  SmartFixHubAnalyticsService,
  PlanClaim,
  assistanceType,
} from '@self-fix/data-access';
import { ButtonComponent } from '@domgen/dgx-fe-components/button';
import { PlanBannerComponent } from '../../components/plan-banner/plan-banner.component';
import { PlanSelectionComponent } from '../../components/plan-selection/plan-selection.component';
import { mergeMap, of, take, tap } from 'rxjs';
import { HomepageComponent } from '../../components/homepage/homepage.component';
import { ENVIRONMENT } from '@domgen/dgx-fe-config';

@Component({
  standalone: true,
  imports: [
    ArticleSearchFormComponent,
    AsyncPipe,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    GridSectionComponent,
    NgClass,
    NgIf,
    CommonModule,
    IconComponent,
    CategoryAccordionBlockComponent,
    MultipleButtonContentCardComponent,
    PopularLinkListBlockComponent,
    VideoListBlockComponent,
    ButtonComponent,
    PlanBannerComponent,
    PlanSelectionComponent,
    HomepageComponent,
  ],
  providers: [ApplianceDetailsComponentService],
  templateUrl: './appliance-details.component.html',
  styleUrls: ['./appliance-details.component.scss'],
})
@SeoPage()
export class ApplianceDetailsComponent implements OnInit {
  constructor(
    public readonly service: ApplianceDetailsComponentService,
    private smartFixAnalytics: SmartFixHubAnalyticsService,
    @Inject(ENVIRONMENT) public environment: any
  ) {}

  ngOnInit(): void {
    if (this.environment) {
      this.service.activePlanNumber$
        .pipe(
          take(1),
          mergeMap((planNumber: string) => {
            if (!planNumber || planNumber.length === 0) {
              this.onShowSelectionPage();
              return of(null);
            } else {
              return this.service.assistance$;
            }
          }),
          take(1),
          tap((assistance: assistanceType | null) => {
            if (!assistance || assistance === 'None') {
              this.onShowSelectionPage();
            } else {
              this.trackHomePageGA();
            }
          })
        )
        .subscribe();
    }
  }

  onClick(params: [string, string]) {
    const [applianceCode, applianceName] = params;
    this.sendAppTypeGA(applianceName);
    this.service.navigate(applianceCode);
  }

  onSelectPlan(plan: PlanClaim) {
    this.service.setActivePlan(plan.plan_number);
  }

  isPlanSelection = false;

  onShowSelectionPage() {
    this.isPlanSelection = true;
    this.trackPlanSelectionPageGA();
    window.scrollY = 0;
  }

  onPlanBannerClick() {
    this.isPlanSelection = true;
    this.sendTopClickEventGA();
    this.trackPlanSelectionPageGA();
    window.scrollY = 0;
  }

  confirmPlanSelection() {
    this.assistance$.pipe(take(1)).subscribe((assistance: assistanceType) => {
      this.sendPlanSelectionEventGA(assistance);

      this.isPlanSelection = false;
      window.scrollY = 0;
      this.trackHomePageGA();
    });
  }

  public readonly assistance$ = this.service.assistance$;

  selectAssistance(assistance: assistanceType) {
    this.service.setAssistance(assistance);
  }

  trackHomePageGA(): void {
    this.smartFixAnalytics.trackEvent({
      pagename: 'claims-sf-hub-dashboard',
      event: 'spa-pageview',
      category: 'claims-2.0',
    });
  }

  trackPlanSelectionPageGA(): void {
    this.smartFixAnalytics.trackEvent({
      pagename: 'claims-sf-hub-appliance-intent',
      event: 'spa-pageview',
      category: 'claims-2.0',
    });
  }

  sendAppTypeGA(applianceName: string) {
    this.smartFixAnalytics.trackEvent({
      event: 'genericGAEvent',
      category: 'claims-2.0',
      eventCategory: 'sf-hub',
      eventAction: 'appliance tile - clicked',
      eventLabel: applianceName + ' clicked',
      pagename: 'claims-sf-hub-dashboard',
    } as any);
  }

  sendTopClickEventGA() {
    this.smartFixAnalytics.trackEvent({
      event: 'genericGAEvent',
      category: 'claims-2.0',
      eventCategory: 'navigation menu',
      eventAction: 'top bar nav',
      eventLabel: 'Change Product',
      pagename: 'claims-sf-hub-dashboard',
    } as any);
  }

  sendPlanSelectionEventGA(eventLabel: string) {
    this.smartFixAnalytics.trackEvent({
      event: 'genericGAEvent',
      category: 'claims-2.0',
      eventCategory: 'sf-hub',
      eventAction: 'search repair and how-to guides',
      eventLabel,
      pagename: 'claims-sf-hub-appliance-intent',
    } as any);
  }
}
